import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiEye,
  HiArrowDownTray,
  HiTrash,
  HiPencil,
  HiXMark,
  HiDocumentText,
  HiMagnifyingGlass,
  HiShare,
} from 'react-icons/hi2';
import { deleteSubmission } from '../../services/productDevelopmentService';
import { toast } from 'react-toastify';
import ShareModal from './ShareModal';
import SubmissionPreviewModal from './SubmissionPreviewModal';

const STAGE_NAMES = {
  idea: 'Idea Validation',
  research: 'Market Research',
  prototype: 'Prototype Development',
  development: 'Product Development',
  mvp: 'MVP Launch',
};

const SubmissionsTable = ({ submissions, loading, onRefresh, onEdit }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const formatDate = date => {
    if (!date) return 'N/A';
    if (date.toDate) {
      return date.toDate().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleDelete = async id => {
    try {
      await deleteSubmission(id);
      toast.success('Submission deleted successfully');
      onRefresh();
      setDeleteConfirm(null);
    } catch (error) {
      toast.error('Failed to delete submission');
    }
  };

  const handleDownload = submission => {
    // Create a downloadable document with submission data
    const content = `
Product Development Submission Report
${'='.repeat(50)}

Stage: ${STAGE_NAMES[submission.stage] || submission.stageName}
Date: ${formatDate(submission.createdAt)}
${'='.repeat(50)}

${Object.entries(submission)
  .filter(
    ([key]) => !['id', 'userId', 'userEmail', 'createdAt', 'updatedAt', 'attachments'].includes(key)
  )
  .map(([key, value]) => {
    const label = key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
    return `${label}:\n${value || 'N/A'}\n`;
  })
  .join('\n')}

${
  submission.attachments && submission.attachments.length > 0
    ? `\nAttachments:\n${submission.attachments.map(att => `- ${att.name}`).join('\n')}`
    : ''
}
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${STAGE_NAMES[submission.stage] || submission.stageName}_${formatDate(submission.createdAt).replace(/[^a-zA-Z0-9]/g, '_')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Submission downloaded');
  };

  const handleShare = submission => {
    setSelectedSubmission(submission);
    setShareOpen(true);
  };

  const handlePreview = submission => {
    setSelectedSubmission(submission);
    setPreviewOpen(true);
  };

  const filteredSubmissions = submissions.filter(sub => {
    const matchesSearch =
      sub.stageName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      Object.values(sub).join(' ').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStage = !stageFilter || sub.stage === stageFilter;
    return matchesSearch && matchesStage;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
          <p className="mt-4 text-sm text-gray-500">Loading submissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="relative flex-1">
          <HiMagnifyingGlass className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search submissions..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          />
        </div>
        <select
          value={stageFilter}
          onChange={e => setStageFilter(e.target.value)}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        >
          <option value="">All Stages</option>
          {Object.entries(STAGE_NAMES).map(([key, name]) => (
            <option key={key} value={key}>
              {name}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700">
                Stage
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700">
                Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700">
                Attachments
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-700">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {filteredSubmissions.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">
                  No submissions found
                </td>
              </tr>
            ) : (
              filteredSubmissions.map((submission, index) => (
                <motion.tr
                  key={submission.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="hover:bg-gray-50"
                >
                  <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-black">
                    {STAGE_NAMES[submission.stage] || submission.stageName}
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                    {formatDate(submission.createdAt)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                    {submission.attachments && submission.attachments.length > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
                        <HiDocumentText className="h-3 w-3" />
                        {submission.attachments.length} file
                        {submission.attachments.length !== 1 ? 's' : ''}
                      </span>
                    ) : (
                      <span className="text-gray-400">No attachments</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handlePreview(submission)}
                        className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50"
                        title="Preview"
                      >
                        <HiEye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDownload(submission)}
                        className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100"
                        title="Download"
                      >
                        <HiArrowDownTray className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleShare(submission)}
                        className="rounded-lg p-2 text-green-600 transition-colors hover:bg-green-50"
                        title="Share"
                      >
                        <HiShare className="h-4 w-4" />
                      </button>
                      {onEdit && (
                        <button
                          onClick={() => onEdit(submission)}
                          className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100"
                          title="Edit"
                        >
                          <HiPencil className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteConfirm(submission)}
                        className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50"
                        title="Delete"
                      >
                        <HiTrash className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
            >
              <h3 className="mb-2 text-lg font-semibold text-black">Delete Submission</h3>
              <p className="mb-6 text-sm text-gray-600">
                Are you sure you want to delete this submission? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm.id)}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modals */}
      <SubmissionPreviewModal
        isOpen={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setSelectedSubmission(null);
        }}
        submission={selectedSubmission}
      />

      <ShareModal
        isOpen={shareOpen}
        onClose={() => {
          setShareOpen(false);
          setSelectedSubmission(null);
        }}
        submission={selectedSubmission}
      />
    </div>
  );
};

export default SubmissionsTable;
